import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { clientsApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { Plus, Building2, Pencil, Trash2, Camera } from 'lucide-react';
import { cn } from '../lib/utils';
import { useClient } from '../context/ClientContext';

const API_URL = '';

const ClientsPage = () => {
  const { fetchClients: refreshGlobalClients } = useClient();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', notes: '' });
  const fileInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null);

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try { const r = await clientsApi.getAll(); setClients(r.data); } catch { toast.error('Error'); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientsApi.update(editingClient.client_id, form);
        toast.success('Cliente actualizado');
      } else {
        await clientsApi.create(form);
        toast.success('Cliente creado');
      }
      setDialogOpen(false);
      setEditingClient(null);
      setForm({ name: '', company: '', email: '', phone: '', notes: '' });
      fetchClients();
      refreshGlobalClients();
    } catch { toast.error('Error al guardar'); }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setForm({ name: client.name, company: client.company, email: client.email, phone: client.phone, notes: client.notes });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try { await clientsApi.delete(id); toast.success('Eliminado'); fetchClients(); refreshGlobalClients(); } catch { toast.error('Error'); }
  };

  const handlePhotoUpload = async (e, clientId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', 'client');
    formData.append('entity_id', clientId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/upload-profile-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) { toast.success('Foto actualizada'); fetchClients(); }
      else toast.error('Error al subir foto');
    } catch { toast.error('Error al subir foto'); }
    setUploadingFor(null);
  };

  if (loading) return <DashboardLayout title="Clientes"><div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" /></div></DashboardLayout>;

  return (
    <DashboardLayout title="Clientes">
      <div className="space-y-6" data-testid="clients-page">
        <div className="flex justify-between items-center">
          <p className="text-zinc-400">{clients.length} clientes registrados</p>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditingClient(null); setForm({ name: '', company: '', email: '', phone: '', notes: '' }); } }}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-client-btn"><Plus size={18} className="mr-2" />Nuevo Cliente</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-white/10 text-white">
              <DialogHeader><DialogTitle>{editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <div><Label className="text-zinc-300">Nombre</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="bg-[#1A1A1A] border-transparent" data-testid="client-name-input" /></div>
                <div><Label className="text-zinc-300">Empresa</Label><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-zinc-300">Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                  <div><Label className="text-zinc-300">Telefono</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                </div>
                <div><Label className="text-zinc-300">Notas</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-[#1A1A1A] border-transparent" /></div>
                <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="save-client-btn">{editingClient ? 'Actualizar' : 'Crear Cliente'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map(client => (
            <Card key={client.client_id} className="bg-[#141414] border-white/5 hover:border-white/10 transition-colors" data-testid={`client-card-${client.client_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  {/* Avatar with photo upload */}
                  <div className="relative group">
                    <Avatar className="h-14 w-14 rounded-full">
                      <AvatarImage src={client.picture ? `${API_URL}${client.picture}` : undefined} className="rounded-full object-cover" />
                      <AvatarFallback className="bg-orange-600 text-white text-lg rounded-full">{client.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <button
                      onClick={() => { setUploadingFor(client.client_id); fileInputRef.current?.click(); }}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      data-testid={`upload-photo-${client.client_id}`}
                    >
                      <Camera size={16} className="text-white" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate">{client.name}</h3>
                    {client.company && <p className="text-sm text-zinc-400">{client.company}</p>}
                    {client.email && <p className="text-xs text-zinc-500 mt-1">{client.email}</p>}
                    {client.phone && <p className="text-xs text-zinc-500">{client.phone}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(client)} className="text-zinc-400 hover:text-white h-8 w-8"><Pencil size={14} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(client.client_id)} className="text-zinc-400 hover:text-red-400 h-8 w-8"><Trash2 size={14} /></Button>
                  </div>
                </div>
                {client.notes && <p className="text-xs text-zinc-500 mt-3 line-clamp-2">{client.notes}</p>}
              </CardContent>
            </Card>
          ))}
          {clients.length === 0 && (
            <Card className="col-span-full bg-[#141414] border-white/5"><CardContent className="flex flex-col items-center py-12"><Building2 className="text-zinc-600 mb-4" size={48} /><p className="text-zinc-400">No hay clientes</p></CardContent></Card>
          )}
        </div>

        {/* Hidden file input for photo upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => uploadingFor && handlePhotoUpload(e, uploadingFor)}
        />
      </div>
    </DashboardLayout>
  );
};

export default ClientsPage;
